'use client';

import { useCallback, useEffect, useState } from 'react';
import { Bell, CheckCheck } from 'lucide-react';
import {
  listarAvisos, marcarAvisoLeido, marcarTodosLeidos, type Aviso,
} from '@/lib/avisos';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

function fmt(iso: string): string {
  const d = new Date(iso);
  return isNaN(d.getTime()) ? '' : d.toLocaleString('es-CO', {
    day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit',
  });
}

/** Campana de notificaciones in-app: contador + lista + marcar leídas + abrir expediente. */
export default function CampanaAvisos({
  onAbrirExpediente,
}: { onAbrirExpediente?: (id: string) => void }) {
  const [avisos, setAvisos] = useState<Aviso[]>([]);
  const [noLeidos, setNoLeidos] = useState(0);

  const cargar = useCallback(async () => {
    try {
      const r = await listarAvisos();
      setAvisos(r.data);
      setNoLeidos(r.noLeidos);
    } catch {
      /* silencioso: la campana no debe romper la barra */
    }
  }, []);

  // Carga inicial + sondeo cada 60 s.
  useEffect(() => {
    void cargar();
    const t = setInterval(() => void cargar(), 60000);
    return () => clearInterval(t);
  }, [cargar]);

  async function abrir(a: Aviso) {
    if (!a.leida) {
      try { await marcarAvisoLeido(a.id); } catch { /* ignora */ }
      setAvisos((prev) => prev.map((x) => (x.id === a.id ? { ...x, leida: true } : x)));
      setNoLeidos((n) => Math.max(0, n - 1));
    }
    if (a.expediente_id) onAbrirExpediente?.(a.expediente_id);
  }

  async function todasLeidas() {
    try { await marcarTodosLeidos(); } catch { /* ignora */ }
    setAvisos((prev) => prev.map((x) => ({ ...x, leida: true })));
    setNoLeidos(0);
  }

  return (
    <DropdownMenu onOpenChange={(o) => { if (o) void cargar(); }}>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="icon" className="relative rounded-full" aria-label="Notificaciones">
          <Bell className="size-5" />
          {noLeidos > 0 && (
            <span className="absolute -right-0.5 -top-0.5 flex min-w-4 items-center justify-center rounded-full bg-destructive px-1 text-[10px] font-bold leading-4 text-white">
              {noLeidos > 9 ? '9+' : noLeidos}
            </span>
          )}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-80 p-0">
        <div className="flex items-center justify-between border-b px-3 py-2">
          <span className="text-sm font-medium">Notificaciones</span>
          {noLeidos > 0 && (
            <Button variant="ghost" size="sm" className="h-7 text-xs" onClick={todasLeidas}>
              <CheckCheck className="size-3.5" /> Marcar leídas
            </Button>
          )}
        </div>
        <div className="max-h-96 overflow-y-auto">
          {avisos.length === 0 ? (
            <p className="px-3 py-8 text-center text-sm text-muted-foreground">Sin notificaciones.</p>
          ) : (
            avisos.map((a) => (
              <button
                key={a.id}
                onClick={() => abrir(a)}
                className={cn(
                  'flex w-full flex-col items-start gap-0.5 border-b px-3 py-2 text-left transition-colors hover:bg-accent',
                  !a.leida && 'bg-primary/5',
                )}
              >
                <span className="flex w-full items-center gap-2 text-sm font-medium">
                  {!a.leida && <span className="size-2 shrink-0 rounded-full bg-primary" />}
                  {a.titulo}
                </span>
                <span className="text-xs text-muted-foreground">{a.mensaje}</span>
                <span className="text-[10px] text-muted-foreground">{fmt(a.fecha_creacion)}</span>
              </button>
            ))
          )}
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
